import React, { useEffect, useMemo, useRef, useState } from "react"
import { Box, Grid, MenuItem, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { faFileArrowDown } from "@fortawesome/pro-light-svg-icons"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import {
    GROW_CELL_CLASS,
    useGridLayoutStyles,
} from "../../../../../../shared/components/table/useGridLayoutStyles"
import { COLUMN_SIZE } from "../../../../../../shared/constants/table/config"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    fetchMeetingServiceOrdersForSupport,
    MeetingServiceOrdersForSupportResponse,
    SupportMeetingServiceQuery,
} from "../meetingServiceApi"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { ActionBarItem, FloatingActionBar } from "../../../../../../shared/components/table/FloatingActionBar"
import { MeetingServiceOrder } from "../../../../../company/reports/accounting/meetingService/meetingServiceTypes"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import OrderStatusBadge from "../../../../../../shared/components/badges/OrderStatusBadge"
import { OrderStatus } from "../../../../../merchant/controlKitchenTypes"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { DateTime } from "luxon"
import {
    defaultMeetingServiceFilterOptionsList,
    FiltersDrawerMeetingService,
    MeetingServiceFilterProps,
} from "../../../../../supplier/reporting/accounting/meetingService/components/FiltersDrawerMeetingService"
import useActiveFiltersCount from "../../../../../supplier/reporting/accounting/meetingService/hooks/useActiveFiltersCount"
import { useExportMeetingService } from "../../../../../supplier/reporting/accounting/meetingService/hooks/useExportMeetingService"
import { OrderIdInput } from "../../../../../supplier/reporting/accounting/components/inputFilters/OrderIdInput"
import { SUPPORT_REPORTS_ROUTE } from "../../../../../../constants/routes"
import { StandardFilterButton } from "../../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"

const useStyles = makeStyles()(() => ({
    table: {
        ".MuiTableCell-root": {
            paddingLeft: "1rem",
            paddingRight: "1rem",
        },
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
}))

const selectMeetingService = (data: MeetingServiceOrdersForSupportResponse) => data?.orders || []

export function SupportMeetingService() {
    const { classes } = useStyles()
    const { classes: gridClasses } = useGridLayoutStyles()
    const { l10n } = useAppLocalization()

    const [drawerOpen, setDrawerOpen] = useState(false)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [selectedRows, setSelectedRows] = useState<MeetingServiceOrder[]>()
    const { startDate, endDate } = useInitialDateFilters()
    const [filtersList, setFiltersList] = useState<MeetingServiceFilterProps>({
        ...defaultMeetingServiceFilterOptionsList,
        startBookedDate: startDate,
        endBookedDate: endDate,
    })

    const { activeCount } = useActiveFiltersCount(filtersList)

    const tableInstanceRef = useRef<MRT_TableInstance<MeetingServiceOrder>>(null)

    const meetingServiceResult = useApiQuery(fetchMeetingServiceOrdersForSupport, {
        queryName: SupportMeetingServiceQuery.MEETING_SERVICE_ORDERS_FOR_SUPPORT,
        dependencies: {
            ...filtersList,
            startBookedDate: filtersList.startBookedDate?.toISODate(),
            endBookedDate: filtersList.endBookedDate?.toISODate(),
            startDeliveryDate: filtersList.startDeliveryDate?.toISODate(),
            endDeliveryDate: filtersList.endDeliveryDate?.toISODate(),
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            satelliteIds: filtersList.satelliteIds?.join(","),
            buyerPartyIds: filtersList.buyerPartyIds?.join(","),
            paymentMethod: filtersList.paymentMethod?.join(","),
        },
        select: selectMeetingService,
    })

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    const { ApiQueryTable, columnFactory } = useApiQueryTable(meetingServiceResult)

    const columns = useMemo<MRT_ColumnDef<MeetingServiceOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("support-accounting-meeting-service-table-order-id-column"),
                accessorKey: "id",
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("support-accounting-meeting-service-table-booked-date-column"),
                    accessorKey: "accountingDetails.bookedTime",
                },
                (row) => row?.accountingDetails?.bookedTime,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("support-accounting-meeting-service-table-delivery-time-column"),
                    accessorKey: "deliveryDetails.time",
                },
                (row) => row?.deliveryDetails?.time,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("support-accounting-meeting-service-table-employee-column"),
                accessorKey: "customer.displayName",
                size: COLUMN_SIZE.XL,
                muiTableHeadCellProps: { className: GROW_CELL_CLASS },
                muiTableBodyCellProps: { className: GROW_CELL_CLASS },
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={
                            <>
                                <Typography variant="body2" component="span" textTransform="uppercase">
                                    {row.original.customer.initials}
                                    {", "}
                                </Typography>
                                <Typography variant="body2" component="span">
                                    {row.original.customer.displayName}
                                </Typography>
                            </>
                        }
                        subtitle1={row.original.meetingDetails.meetingName}
                    />
                ),
            }),
            columnFactory.statusColumn({
                header: l10n.getString("support-accounting-meeting-service-table-status"),
                accessorKey: "orderStatus",
                Cell: ({ row }) => {
                    return <OrderStatusBadge placement="table" status={row.original.orderStatus as OrderStatus | ""} />
                },
                filterSelectOptions: [
                    {
                        value: "CONFIRMED",
                        text: l10n.getString("production-order-status-confirmed"),
                    },
                    {
                        value: "RECEIVED",
                        text: l10n.getString("production-order-status-received"),
                    },
                    {
                        value: "CANCELLED",
                        text: l10n.getString("production-order-status-cancelled"),
                    },
                    {
                        value: "READY",
                        text: l10n.getString("production-order-status-ready"),
                    },
                ],
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("support-accounting-meeting-service-table-amount-column"),
                    id: "amount",
                },
                (row) => row.amount,
            ),
        ],
        [l10n, columnFactory],
    )

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }

    const { handleExportRows } = useExportMeetingService(
        filtersList.startBookedDate?.toISODate() || DateTime.now().toISODate(),
        filtersList.endBookedDate?.toISODate() || DateTime.now().toISODate(),
    )

    return (
        <>
            <LocalizedStrict id="support-accounting-meeting-service-header" attrs={{ title: true }}>
                <PageHeader page={Page.SUPPORT_ACCOUNTING_MEETING_SERVICE} title="Meeting Service">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-orders">
                            <Breadcrumb>Orders</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-accounting-meeting-service">
                            <Breadcrumb>Meeting Service</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Box className={classes.table}>
                <ApiQueryTable
                    layoutMode="grid"
                    muiTablePaperProps={{ className: gridClasses.pinned }}
                    tableInstanceRef={tableInstanceRef}
                    columns={columns}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection
                    initialState={{ density: "spacious" }}
                    state={{
                        rowSelection,
                    }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <LocalizedStrict id="support-accounting-meeting-service-table-show-receipt">
                            <MenuItem
                                onClick={() => {
                                    closeMenu()
                                    // handleShowReceipt(row.original.id)
                                }}
                                key={`show-receipt-${row.original.id}`}
                            >
                                Show receipt
                            </MenuItem>
                        </LocalizedStrict>,
                    ]}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter variant="right" table={table}>
                            <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />
                            <StandardExportButton
                                onClick={() => {
                                    handleExportRows(tableInstanceRef.current?.getRowModel().rows)
                                }}
                            />
                        </StandardTableFilter>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <StandardDateRangeFilter
                                value={{
                                    startDate: filtersList.startBookedDate!,
                                    endDate: filtersList.endBookedDate!,
                                }}
                                onChange={(newValue) => {
                                    setFiltersList((prev) => ({
                                        ...prev,
                                        startBookedDate: newValue.startDate,
                                        endBookedDate: newValue.endDate,
                                    }))
                                }}
                            />
                            <OrderIdInput
                                value={filtersList.orderId ?? ""}
                                onChange={(orderId) => {
                                    setFiltersList((prev) => ({
                                        ...prev,
                                        orderId,
                                    }))
                                }}
                            />
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => (
                        <TableSummaryWrap>
                            <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                <TableNumberOfItemsBox
                                    data={table.getFilteredRowModel().rows}
                                    isLoading={meetingServiceResult.isLoading}
                                    title="Number of orders"
                                />
                            </LocalizedStrict>

                            <TableTotalAmountBox
                                data={table.getFilteredRowModel().rows}
                                isLoading={meetingServiceResult.isLoading}
                                accessor={(row) => row.original.amount}
                            />
                        </TableSummaryWrap>
                    )}
                />
            </Box>
            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict
                        id="support-accounting-meeting-service-table-floating-action-bar-export"
                        attrs={{ title: true }}
                    >
                        <ActionBarItem
                            icon={faFileArrowDown}
                            title="Export"
                            onClick={() => handleExportRows(tableInstanceRef.current?.getSelectedRowModel().rows)}
                        />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}

            <FiltersDrawerMeetingService
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />
        </>
    )
}
